import React, { useEffect, useRef, useState } from 'react';
import { Close, Sliders } from './Icons';
import { useSensory } from '../context/SensoryContext';
import './SensoryPanel.css';

const Toggle = ({ id, label, hint, checked, onChange }) => (
  <div className="sp-row">
    <div>
      <label htmlFor={id} className="sp-label">
        {label}
      </label>
      <p className="sp-hint">{hint}</p>
    </div>
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      className="sp-switch"
      onClick={() => onChange(!checked)}
    >
      <span className="sp-thumb" />
    </button>
  </div>
);

// Floating panel that lets each visitor tune the site to their senses.
const SensoryPanel = () => {
  const [open, setOpen] = useState(false);
  const { settings, update, reset, scales } = useSensory();
  const panelRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    panelRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const onClick = (e) => {
      if (!panelRef.current?.contains(e.target) && !triggerRef.current?.contains(e.target)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onClick);
    };
  }, [open]);

  return (
    <div className="sensory">
      <button
        ref={triggerRef}
        type="button"
        className="sp-trigger"
        aria-expanded={open}
        aria-controls="sensory-panel"
        onClick={() => setOpen((o) => !o)}
      >
        <Sliders />
        <span>Sensory settings</span>
      </button>

      <div
        id="sensory-panel"
        ref={panelRef}
        className={`sp-panel ${open ? 'is-open' : ''}`}
        role="dialog"
        aria-label="Sensory settings"
        tabIndex={-1}
        hidden={!open}
      >
        <div className="sp-head">
          <div>
            <h2>Make it yours</h2>
            <p>Tune the site to how you browse best. Saved on this device.</p>
          </div>
          <button type="button" className="icon-btn" aria-label="Close sensory settings" onClick={() => setOpen(false)}>
            <Close />
          </button>
        </div>

        <fieldset className="sp-row sp-segment-row">
          <legend className="sp-label">Theme</legend>
          <div className="sp-segment">
            {['dark', 'light'].map((t) => (
              <button
                key={t}
                type="button"
                aria-pressed={settings.theme === t}
                onClick={() => update({ theme: t })}
              >
                {t === 'dark' ? 'Dark' : 'Light'}
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset className="sp-row sp-segment-row">
          <legend className="sp-label">Text size</legend>
          <div className="sp-segment">
            {scales.map((s, i) => (
              <button
                key={s}
                type="button"
                aria-pressed={settings.scale === s}
                aria-label={['Default text size', 'Large text', 'Extra large text'][i]}
                onClick={() => update({ scale: s })}
                style={{ fontSize: `${0.85 + i * 0.15}rem` }}
              >
                A
              </button>
            ))}
          </div>
        </fieldset>

        <Toggle
          id="sp-motion"
          label="Reduce motion"
          hint="Stops animations, parallax and the moving background."
          checked={settings.motion === 'reduce'}
          onChange={(v) => update({ motion: v ? 'reduce' : 'full' })}
        />
        <Toggle
          id="sp-calm"
          label="Calm colours"
          hint="Softer, low-saturation accents with no glow."
          checked={settings.calm}
          onChange={(v) => update({ calm: v })}
        />
        <Toggle
          id="sp-font"
          label="Readable font"
          hint="Atkinson Hyperlegible, designed for low vision and dyslexia."
          checked={settings.font === 'readable'}
          onChange={(v) => update({ font: v ? 'readable' : 'default' })}
        />

        <button type="button" className="sp-reset" onClick={reset}>
          Reset to my system defaults
        </button>
      </div>
    </div>
  );
};

export default SensoryPanel;
